import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useToday } from '@resource-kit/controllers/react';
import { addCalendarDays } from '@lib/calendar-day';
import {
  rowSize,
  type BoardDay,
  type BoardLine,
  type BoardStage,
  type BoardWindow,
} from './boardModel';
import type { DeliveryFlowWire } from './boardWire';
import type { RouteEtaSummary } from './etaSignals';
import { EXECUTOR_LABEL } from './flowCopy';
import { formatBatchDay, formatMonthDay } from './formatBatchDay';
import { deriveRowCopy, rowTime, type CellTone } from './rowCopy';

export { GATE_LABEL } from './rowCopy';

// Literal keys. `t()` reads them by lookup, which the i18n source-coverage
// scan cannot follow, so `__tests__/boardLabels.test.ts` pins them instead.
export const STAGE_LABEL = {
  awaiting_dispatch: 'dispatch.board.stage.awaiting_dispatch',
  awaiting_assignment: 'dispatch.board.stage.awaiting_assignment',
  awaiting_departure: 'dispatch.board.stage.awaiting_departure',
  en_route: 'dispatch.board.stage.en_route',
  delivered: 'dispatch.board.stage.delivered',
  exception_or_cancelled: 'dispatch.board.stage.exception_or_cancelled',
} as const satisfies Record<BoardStage, string>;

type FlowAction = DeliveryFlowWire['actions'][number]['action'];

/**
 * The action into a column, as the board header says it. Not the settings-page
 * `deliveryFlow.action.*` words: 「出发（第一站开始）」 spells out what the
 * driver does.
 */
export const COLUMN_ACTION_LABEL = {
  dispatch: 'dispatch.board.columnAction.dispatch',
  assign_driver: 'dispatch.board.columnAction.assign_driver',
  depart: 'dispatch.board.columnAction.depart',
  deliver: 'dispatch.board.columnAction.deliver',
} as const satisfies Record<FlowAction, string>;

export const BATCH_WORD_LABEL = {
  shipping_date: 'dispatch.board.batchWord.shipping_date',
} as const satisfies Record<DeliveryFlowWire['batch'], string>;

const TONE_TAG = {
  past: 'dispatch.board.yesterday',
  today: 'dispatch.board.today',
} as const;

/** Stacked-bar colour per column: the delivery-status tokens (ADR-0043). */
const STACK_CLASS = {
  awaiting_dispatch: 'bg-status-unassigned',
  awaiting_assignment: 'bg-warning',
  awaiting_departure: 'bg-status-assigned',
  en_route: 'bg-status-in-progress',
  delivered: 'bg-success',
  exception_or_cancelled: 'bg-destructive',
} as const satisfies Record<BoardStage, string>;

/** Only the active columns carry a heat tint. */
const HEAT_STAGES: readonly BoardStage[] = [
  'awaiting_dispatch',
  'awaiting_assignment',
  'awaiting_departure',
  'en_route',
];

/** Status colour goes on the dot only; the sub-text stays in body text (ADR-0043). */
const CELL_TONE_TEXT = {
  danger: 'text-foreground',
  warning: 'text-foreground',
  muted: 'text-muted-foreground',
} as const satisfies Record<CellTone, string>;

const CELL_TONE_DOT = {
  danger: 'bg-destructive',
  warning: 'bg-warning',
  muted: null,
} as const satisfies Record<CellTone, string | null>;

const TODAY_ROW_STYLE: CSSProperties = {
  backgroundImage:
    'linear-gradient(90deg, color-mix(in srgb, var(--accent) 16%, transparent), transparent 30%)',
};

/** `0.08 + 0.32 × n / max` of the accent colour, `max` over the active columns. */
function heatStyle(day: BoardDay, stage: BoardStage): CSSProperties | undefined {
  const count = day.counts[stage];
  if (!HEAT_STAGES.includes(stage) || count === 0) return undefined;
  const max = Math.max(1, ...HEAT_STAGES.map((s) => day.counts[s]));
  const percent = Math.round((0.08 + (0.32 * count) / max) * 100);
  return { background: `color-mix(in srgb, var(--accent) ${percent}%, transparent)` };
}

function isWeekendRun(from: string, to: string): boolean {
  for (let date = from; date <= to; date = addCalendarDays(date, 1)) {
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    if (weekday !== 0 && weekday !== 6) return false;
  }
  return true;
}

function spanDays(from: string, to: string): number {
  return (
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
  );
}

interface BatchBoardProps {
  lines: BoardLine[];
  stages: readonly BoardStage[];
  selected: { date: string; stage: BoardStage };
  onSelect: (date: string, stage: BoardStage) => void;
  loading: boolean;
  isError: boolean;
  /** The tenant flow; undefined while it loads or when its GET failed. */
  flow?: DeliveryFlowWire;
  now: Date;
  /** Today's route ETA summary; feeds today's en-route copy only. */
  eta?: RouteEtaSummary;
  /** The board window, for the batch column hint. */
  window: BoardWindow;
  /** Sold-out count for the selected day's row only (#2518). */
  soldOut?: { date: string; count: number };
}

export function BatchBoard({
  lines,
  stages,
  selected,
  onSelect,
  loading,
  isError,
  flow,
  now,
  eta,
  window: boardWin,
  soldOut,
}: BatchBoardProps) {
  const { t, i18n } = useTranslation();
  const formatDay = (date: string) => formatBatchDay(date, i18n.language);
  const formatMd = (date: string) => formatMonthDay(date, i18n.language);
  const today = useToday();
  const colSpan = stages.length + 2;

  const hintCount = spanDays(boardWin.labelFrom, boardWin.labelTo);
  const batchHint =
    boardWin.anchor === 'today'
      ? t('dispatch.board.batchHint', { count: hintCount })
      : t('dispatch.board.batchHintShifted', {
          from: formatMd(boardWin.labelFrom),
          count: hintCount,
        });

  const routeStages: readonly BoardStage[] = stages.filter((s) => s !== 'exception_or_cancelled');

  const renderStageHeader = (stage: BoardStage) => {
    const action = flow?.actions.find((candidate) => candidate.to === stage);
    let actionRow: { text: string; who: string | null } | null = null;
    if (action) {
      actionRow = {
        text: t(COLUMN_ACTION_LABEL[action.action]),
        who: t(EXECUTOR_LABEL[action.executor]),
      };
    } else if (stage === 'awaiting_dispatch') {
      actionRow = { text: t('dispatch.board.intake'), who: t('dispatch.board.intakeWho') };
    } else if (stage === 'exception_or_cancelled') {
      actionRow = { text: t('dispatch.board.bypass'), who: null };
    }
    const i = routeStages.indexOf(stage);
    const last = routeStages.length - 1;
    const routeMark =
      stage === 'exception_or_cancelled' ? (
        <span
          aria-hidden
          data-testid="dispatch-board-bypass-dot"
          className="mx-auto mt-1 block size-3.5 rounded-full border-2 border-dashed border-destructive"
        />
      ) : (
        <span
          aria-hidden
          data-testid={`dispatch-board-route-${stage}`}
          className="mt-1 flex items-center"
        >
          <span className={`h-[3px] flex-1 ${i === 0 ? '' : 'bg-route'}`} />
          <span
            className={`size-3.5 shrink-0 rounded-full border-[3px] border-ring ${
              i === 0 || i === last ? 'bg-ring' : 'bg-card'
            }`}
          />
          <span className={`h-[3px] flex-1 ${i === last ? '' : 'bg-route'}`} />
        </span>
      );
    return (
      <th
        key={stage}
        scope="col"
        data-testid={`dispatch-board-col-${stage}`}
        className="px-0 pt-2.5 pb-2 text-center align-top font-normal"
      >
        <span
          data-testid={`dispatch-board-caption-${stage}`}
          className="flex h-9 flex-col items-center justify-end gap-0.5 px-1.5 text-[11px] text-muted-foreground"
        >
          {actionRow && (
            <>
              <span className="max-w-full truncate" title={actionRow.text}>
                {actionRow.text}
              </span>
              {actionRow.who && (
                <span className="rounded-sm border border-border px-1.5 text-[10.5px] leading-4">
                  {actionRow.who}
                </span>
              )}
            </>
          )}
        </span>
        {routeMark}
        <span className="mt-0.5 block px-2.5 text-[12.5px] font-semibold">
          {t(STAGE_LABEL[stage])}
        </span>
      </th>
    );
  };

  const renderDay = (day: BoardDay, tone: 'past' | 'today' | 'future') => {
    const time = rowTime(day.date, today);
    const size = rowSize(day);
    const copy = deriveRowCopy({
      day,
      today,
      now,
      flow,
      eta,
      soldOutCount: soldOut?.date === day.date ? soldOut.count : 0,
      language: i18n.language,
      t,
    });
    return (
      <tr
        key={day.date}
        data-testid={`dispatch-board-row-${day.date}`}
        className={`border-t border-border/50 ${time === 'past' ? 'opacity-55' : ''}`}
        style={time === 'today' ? TODAY_ROW_STYLE : undefined}
      >
        <th scope="row" className="px-3 py-2 text-left align-middle font-normal">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold whitespace-nowrap">
            {formatDay(day.date)}
            {tone === 'today' ? (
              <span className="rounded-sm bg-primary/10 px-1.5 text-[11px] leading-[18px] font-medium text-foreground">
                {t(TONE_TAG.today)}
              </span>
            ) : tone === 'past' ? (
              <span className="text-xs font-normal text-muted-foreground">{t(TONE_TAG.past)}</span>
            ) : null}
            {day.restDay ? (
              <span
                data-testid={`dispatch-board-rest-badge-${day.date}`}
                className="text-xs font-normal text-muted-foreground"
              >
                {t('dispatch.board.restDayBadge')}
              </span>
            ) : null}
          </span>
          <span className="block text-[12.5px] text-muted-foreground">
            {t('dispatch.board.rowTotal', { count: size })}
          </span>
          <span
            data-testid={`dispatch-board-stack-${day.date}`}
            className="mt-1 flex h-1 overflow-hidden rounded-sm bg-muted"
          >
            {stages.map((stage) => (
              <span
                key={stage}
                className={STACK_CLASS[stage]}
                style={{ width: `${size === 0 ? 0 : (day.counts[stage] / size) * 100}%` }}
              />
            ))}
          </span>
        </th>
        {stages.map((stage) => {
          const pressed = selected.date === day.date && selected.stage === stage;
          const count = day.counts[stage];
          const sub = copy.cells[stage];
          return (
            <td key={stage} className="px-[3px] py-1 align-middle">
              <button
                type="button"
                aria-pressed={pressed}
                aria-label={
                  count === 0
                    ? t('dispatch.board.cellNone', {
                        date: formatDay(day.date),
                        stage: t(STAGE_LABEL[stage]),
                      })
                    : t('dispatch.board.cell', {
                        date: formatDay(day.date),
                        stage: t(STAGE_LABEL[stage]),
                        count,
                      })
                }
                data-testid={`dispatch-board-cell-${day.date}-${stage}`}
                onClick={() => onSelect(day.date, stage)}
                style={heatStyle(day, stage)}
                className={`flex min-h-10 w-full min-w-0 flex-col justify-center rounded-lg px-2 py-1.5 text-left ${
                  pressed
                    ? 'outline-2 -outline-offset-2 outline-primary outline-solid'
                    : 'hover:outline-1 hover:-outline-offset-1 hover:outline-border hover:outline-solid focus-visible:outline-1 focus-visible:outline-border focus-visible:outline-solid'
                }`}
              >
                <span
                  className={
                    count === 0
                      ? 'text-[13px] leading-tight text-muted-foreground'
                      : 'text-[15px] leading-tight font-semibold tabular-nums'
                  }
                >
                  {count === 0 ? '—' : count}
                </span>
                {sub ? (
                  <span
                    className={`flex min-w-0 items-center gap-1 text-[11px] ${CELL_TONE_TEXT[sub.tone]}`}
                  >
                    {CELL_TONE_DOT[sub.tone] ? (
                      <span
                        aria-hidden
                        className={`size-1.5 shrink-0 rounded-full ${CELL_TONE_DOT[sub.tone]}`}
                      />
                    ) : null}
                    <span className="min-w-0 truncate">{sub.text}</span>
                  </span>
                ) : null}
              </button>
            </td>
          );
        })}
        <td
          data-testid={`dispatch-board-next-${day.date}`}
          className="px-3 py-2 align-middle text-xs"
        >
          <span
            className={`block ${copy.next.firstTone === 'default' ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            {copy.next.first}
          </span>
          {copy.next.second ? (
            <span className="block text-muted-foreground">{copy.next.second}</span>
          ) : null}
        </td>
      </tr>
    );
  };

  const renderGap = (key: string, testId: string, text: string, badge?: string) => (
    <tr key={key} className="border-t border-border/50">
      <td
        colSpan={colSpan}
        data-testid={testId}
        className="px-3 py-1.5 text-left text-xs text-muted-foreground"
      >
        {badge ? (
          <span className="mr-2 rounded-sm border border-border px-1.5 text-[11px] text-muted-foreground">
            {badge}
          </span>
        ) : null}
        {text}
      </td>
    </tr>
  );

  const collapsedText = (from: string, to: string) => {
    if (from === to) return t('dispatch.board.collapsedOne', { from: formatDay(from) });
    const range = { from: formatMd(from), to: formatMd(to) };
    return isWeekendRun(from, to)
      ? t('dispatch.board.collapsedWeekend', range)
      : t('dispatch.board.collapsed', range);
  };

  return (
    <section
      aria-label={t('dispatch.board.title')}
      data-testid="dispatch-board"
      aria-busy={loading}
      className="overflow-x-auto rounded-xl border border-border/60 bg-card text-card-foreground"
    >
      {isError ? (
        <p role="alert" className="p-4 text-sm text-destructive">
          {t('dispatch.board.error')}
        </p>
      ) : loading ? (
        // The lines are zero-filled until the first GET lands; rendering
        // them would read as "no delivery jobs" on every day.
        <p className="p-4 text-sm text-muted-foreground">{t('dispatch.board.loading')}</p>
      ) : (
        <table className="w-full min-w-[900px] table-fixed border-collapse text-sm">
          <colgroup>
            <col style={{ width: 150 }} />
            {stages.map((stage) => (
              <col key={stage} />
            ))}
            <col style={{ width: 190 }} />
          </colgroup>
          <thead>
            <tr className="border-b border-border">
              <th scope="col" className="px-2.5 pt-2.5 pb-2 text-left align-top font-normal">
                <span className="block text-[12.5px] font-semibold">
                  {t(BATCH_WORD_LABEL[flow?.batch ?? 'shipping_date'])}
                </span>
                <span
                  className="mt-0.5 block truncate text-[11px] text-muted-foreground"
                  data-testid="dispatch-board-batch-hint"
                  title={batchHint}
                >
                  {batchHint}
                </span>
              </th>
              {stages.map(renderStageHeader)}
              <th scope="col" className="px-2.5 pt-2.5 pb-2 text-left align-top font-normal">
                <span className="block text-[12.5px] font-semibold">
                  {t('dispatch.board.nextGate')}
                </span>
                <span
                  className="mt-0.5 block truncate text-[11px] text-muted-foreground"
                  data-testid="dispatch-board-next-gate-hint"
                  title={t('dispatch.board.nextGateHint')}
                >
                  {t('dispatch.board.nextGateHint')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) =>
              line.kind === 'day'
                ? renderDay(line.day, line.tone)
                : line.kind === 'rest'
                  ? renderGap(
                      `rest-${line.date}`,
                      `dispatch-board-rest-${line.date}`,
                      t('dispatch.board.restDay', { date: formatDay(line.date) }),
                      t('dispatch.board.restDayBadge'),
                    )
                  : renderGap(
                      `collapsed-${line.from}`,
                      `dispatch-board-collapsed-${line.from}`,
                      collapsedText(line.from, line.to),
                    ),
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}
